/**
 * Document Service
 * Handles all document-related API operations including uploads, downloads, and listings
 */

import api from '@/core/api/api';

const documentService = {
  /**
   * Upload document for performance extraction (Manager)
   * POST /manage_docs/upload/extract_performance
   * @param {File} file
   * @param {{user_id: string}} userInfo
   * @param {{entity_name?: string, entity_id?: string}} [entityCtx]
   */
  uploadPerformanceExtraction: async (file, userInfo, entityCtx = {}) => {
    try {
      const { presigned_url, document_id, s3_bucket, s3_key } = await getUploadUrl(
        'performance_extraction',
        {
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          uploaded_by: userInfo.user_id,
          firm_id: entityCtx.firm_id,
          ...(entityCtx.entity_name ? { entity_name: entityCtx.entity_name } : {}),
          ...(entityCtx.entity_id   ? { entity_id:   entityCtx.entity_id   } : {}),
        }
      );
      await putToPresignedUrl(presigned_url, file, file.type || 'application/octet-stream');
      return { success: true, document_id, s3_bucket, s3_key };
    } catch (error) {
      console.error('Error uploading performance extraction document:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload performance document');
    }
  },
  /**
   * Upload opportunity documents (Manager only)
   * @param {string} opportunityId - Opportunity ID
   * @param {string} managerId - Manager ID  
   * @param {File} file - File object
   * @param {Object} userInfo - User information
   */
  uploadOpportunityDocument: async (opportunityId, managerId, file, userInfo) => {
    try {
      const contentType = file.type || 'application/octet-stream';
      const { presigned_url, document_id, s3_bucket, s3_key } = await getUploadUrl(
        'opportunity_document',
        {
          file_name: file.name,
          content_type: contentType,
          uploaded_by: userInfo.user_id,
          manager_id: managerId,
          opportunity_id: opportunityId,
          file_size: file.size,
        }
      );
      await putToPresignedUrl(presigned_url, file, contentType);
      return {
        document_id,
        s3_bucket,
        s3_key,
        status: 'uploaded',
      };
    } catch (error) {
      console.error('Error uploading opportunity document:', error);
      throw new Error(error.response?.data?.error || error.message || 'Failed to upload document');
    }
  },

  /**
   * Upload temporary opportunity documents (during creation)
   * @param {string} tempOpportunityId - Temporary opportunity ID
   * @param {string} managerId - Manager ID  
   * @param {File} file - File object
   * @param {Object} userInfo - User information
   */
  uploadTempOpportunityDocument: async (tempOpportunityId, managerId, file, userInfo) => {
    try {
      const contentType = file.type || 'application/octet-stream';
      const { presigned_url, document_id, s3_bucket, s3_key } = await getUploadUrl(
        'temp_opportunity_document',
        {
          file_name: file.name,
          content_type: contentType,
          uploaded_by: userInfo.user_id,
          manager_id: managerId,
          temp_opportunity_id: tempOpportunityId,
          file_size: file.size,
        }
      );
      await putToPresignedUrl(presigned_url, file, contentType);
      return {
        document_id,
        s3_bucket,
        s3_key,
        status: 'uploaded',
      };
    } catch (error) {
      console.error('Error uploading temp opportunity document:', error);
      throw new Error(error.response?.data?.error || error.message || 'Failed to upload temp document');
    }
  },

  /**
   * Move temporary opportunity documents to permanent location
   * @param {string} tempOpportunityId - Temporary opportunity ID
   * @param {string} opportunityId - New opportunity ID
   * @param {string} managerId - Manager ID
   */
  moveTempOpportunityDocuments: async (tempOpportunityId, opportunityId, managerId) => {
    try {
      const response = await api.patch('/manage_docs/upload/move_temp_opport_docs', {
        temp_opportunity_id: tempOpportunityId,
        opportunity_id: opportunityId,
        manager_id: managerId
      });
      return response.data;
    } catch (error) {
      console.error('Error moving temp documents:', error);
      throw new Error(error.response?.data?.error || 'Failed to move temp documents');
    }
  },

  /**
   * Get list of opportunity documents
   * @param {string} opportunityId - Opportunity ID
   */
  getOpportunityDocuments: async (opportunityId) => {
    try {
      const response = await api.get(`/manage_docs/list_opportunity_docs/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunity documents:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch documents');
    }
  },

  /**
   * Download a document by ID
   * @param {string} documentId - Document ID
   */
  downloadDocument: async (documentId) => {
    try {
      const response = await api.get(`/manage_docs/download/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error downloading document:', error);
      throw new Error(error.response?.data?.error || 'Failed to download document');
    }
  },

  /**
   * Get document processing status for polling
   * GET /manage_docs/document_status/{document_id}
   * @param {string} documentId
   */
  getDocumentStatus: async (documentId) => {
    try {
      const response = await api.get(`/manage_docs/document_status/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting document status:', error);
      throw new Error(error.response?.data?.error || 'Failed to get document status');
    }
  },

  /**
   * Delete a document (Manager only)
   * @param {string} documentId - Document ID
   * @param {Object} userInfo - User information
   */
  deleteDocument: async (documentId, userInfo) => {
    try {
      // Add user info as query parameters for authentication
      const params = new URLSearchParams({
        user_id: userInfo.user_id,
        user_type: userInfo.user_type || 'manager'
      });
      
      const response = await api.delete(`/manage_docs/delete/${documentId}?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete document');
    }
  },

  /**
   * Clean up temporary documents (if user cancels opportunity creation).
   * @param {string} tempOpportunityId - Temporary opportunity ID
   * @param {{ user_id: string, user_type?: string }} userInfo - Authenticated manager (same as uploads/deletes)
   */
  cleanupTempDocuments: async (tempOpportunityId, userInfo) => {
    try {
      const response = await documentService.getOpportunityDocuments(tempOpportunityId);
      const documents = response.documents || [];

      for (const doc of documents) {
        try {
          await documentService.deleteDocument(doc.document_id, userInfo);
        } catch (error) {
          console.warn(`Failed to delete temp document ${doc.filename}:`, error);
        }
      }
    } catch (error) {
      console.error('Error cleaning up temp documents:', error);
      console.warn('Failed to cleanup temp documents:', error.message);
    }
  },

  /**
   * Trigger download in browser
   * @param {string} downloadUrl - Presigned download URL
   * @param {string} filename - Original filename
   */
  triggerDownload: (downloadUrl, filename) => {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_blank';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error triggering download:', error);
      // Fallback: open in new tab
      window.open(downloadUrl, '_blank');
    }
  }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file type icon name
 * @param {string} contentType - MIME type
 * @returns {string} Icon name
 */
export function getFileIcon(contentType) {
  if (contentType.includes('pdf')) return 'PictureAsPdf';
  if (contentType.includes('word') || contentType.includes('document')) return 'Description';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'TableChart';
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'Slideshow';
  if (contentType.includes('image')) return 'Image';
  if (contentType.includes('text')) return 'TextSnippet';
  return 'InsertDriveFile';
}

export default documentService;

// ─── Consolidated upload + polling primitives ────────────────────────────────

/**
 * Request a presigned S3 PUT URL and a job_id from the backend.
 * POST /manage_docs/upload/url
 *
 * @param {string} docType - Must match a key in the backend DOCUMENT_TYPES config.
 * @param {Object} params  - { file_name, content_type, uploaded_by, firm_id?, opportunity_id?, temp_opportunity_id?, manager_id?, file_size?, entity_id?, bucket? }
 * @returns {Promise<{ presigned_url: string, job_id: string, s3_bucket: string, s3_key: string }>}
 */
export async function getUploadUrl(docType, params) {
  const response = await api.post('/manage_docs/upload/url', { doc_type: docType, ...params });
  return response.data;
}

/**
 * PUT a File directly to S3 using a presigned URL obtained from getUploadUrl.
 * Throws if the S3 PUT returns a non-2xx status.
 *
 * @param {string} uploadUrl   - Presigned PUT URL.
 * @param {File}   file        - File object to upload.
 * @param {string} contentType - MIME type (should match what was used to generate the URL).
 */
export async function putToPresignedUrl(uploadUrl, file, contentType) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
}

/**
 * Fetch a single job status record from hfr_connect_job_status.
 * GET /manage_docs/upload/{jobId}/status
 *
 * @param {string} jobId
 * @returns {Promise<{ processing_status: string, progress_percentage: number, progress_description: string, result?: Object, error?: string }>}
 */
export async function getJobStatus(jobId) {
  const response = await api.get(`/manage_docs/upload/${jobId}/status`);
  return response.data;
}

/**
 * POST /manage_docs/upload/{jobId}/trigger — action clean_csv_import (after presigned PUT for doc_type bulk_firms).
 * @param {string} jobId
 * @param {Object} [payload] — admin_user_id, dry_run, row_from, row_to
 */
export async function triggerCleanCsvImport(jobId, payload = {}) {
  const response = await api.post(`/manage_docs/upload/${jobId}/trigger`, {
    action: 'clean_csv_import',
    ...payload,
  });
  return response.data;
}

/**
 * Generic polling helper. Calls fetchFn repeatedly until a terminal status is reached
 * or maxAttempts is exhausted.
 *
 * @param {() => Promise<Object>} fetchFn       - Async function that returns the latest status object.
 * @param {Object}                [options]
 * @param {number}  [options.intervalMs=3000]   - Milliseconds between polls.
 * @param {number}  [options.maxAttempts=40]    - Max number of polls before giving up (~2 min at 3 s).
 * @param {(status: Object) => boolean} [options.isTerminal] - Returns true when polling should stop.
 *        Defaults to checking processing_status or status field for 'completed'/'failed'/'error'.
 * @param {(status: Object) => void} [options.onProgress]  - Called with each non-terminal response.
 * @param {AbortSignal} [options.signal]                   - Abort polling when the signal fires.
 * @returns {Promise<Object>} Final status object.
 * @throws {Error} When maxAttempts is reached without a terminal status.
 */
export function pollJob(fetchFn, {
  intervalMs = 3000,
  maxAttempts = 40,
  isTerminal = null,
  onProgress = null,
  signal = null,
} = {}) {
  const defaultIsTerminal = (s) => {
    const st = s?.processing_status || s?.status || '';
    return ['completed', 'failed', 'error', 'complete'].includes(st.toLowerCase());
  };
  const checkTerminal = isTerminal || defaultIsTerminal;

  return new Promise((resolve, reject) => {
    let attempts = 0;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException('Polling aborted', 'AbortError'));
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort);

    const tick = async () => {
      if (signal?.aborted) {
        onAbort();
        return;
      }
      try {
        attempts += 1;
        const status = await fetchFn();

        if (checkTerminal(status)) {
          cleanup();
          resolve(status);
          return;
        }

        if (onProgress) onProgress(status);

        if (attempts >= maxAttempts) {
          cleanup();
          reject(new Error(`Polling timed out after ${maxAttempts} attempts`));
          return;
        }

        timeoutId = setTimeout(tick, intervalMs);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    tick();
  });
}
